<script lang="ts">
    import { tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'

    import 'codemirror/mode/javascript/javascript'

    import Icon from '@iconify/svelte'
    import { cloneDeep } from 'lodash-es'

    import { Breadcrumb } from 'breadcrumb'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    import CodeMirror from 'admin/components/CodeMirror.svelte'

    const crumbs = [
        {
            txt: tgettext('Configuration des métadonnées supplémentaires'),
        },
    ]

    interface EMetaFieldChoice {
        type: 'choice'
        tag: string
        name: string
        choices: {
            tag: string
            text: string
        }[]
    }
    interface EMetaFieldString {
        type: 'string'
        tag: string
        name: string
    }
    interface EMetaConfig {
        id?: string
        fields: (EMetaFieldChoice | EMetaFieldString)[]
        field_order: string[]
    }

    let loading = $state(true)
    let saving = $state(false)
    let config: EMetaConfig
    let value: string = $state('')
    let error: string | undefined = $state(undefined)

    async function loadConfig() {
        loading = true
        let r: any
        try {
            r = await simpleFetch('GET', '/json/wdom-user/1/extra-meta/config')
        } catch (e) {
            console.error('Error:', e)
            notify({
                type: 'danger',
                title: 'Métadonnées',
                text: 'La configuration n’a pas pu être chargée suite à une erreur.',
            })
            loading = false
            return
        }

        if (!r.data.fields || r.data.fields.length < 1) {
            config = {
                fields: [],
                field_order: [],
            }
        } else {
            config = {
                fields: r.data.fields,
                field_order: r.data.field_order,
            }
        }
        value = JSON.stringify(r.data.fields, null, '\t')
        validate(value)
        loading = false
    }
    loadConfig()

    function validate(json: string) {
        let parsed: any
        error = undefined
        try {
            parsed = JSON.parse(json)
        } catch (e) {
            error = String(e)
        }

        if (!error && parsed) {
            let order: string[] = []
            let fields: (EMetaFieldChoice | EMetaFieldString)[] = []

            for (const field of parsed) {
                if (!field.type) {
                    error = 'Champ manquant : « type »'
                    return
                }
                if (!field.tag) {
                    error = 'Champ manquant : « tag »'
                    return
                }
                if (!field.name) {
                    error = 'Champ manquant : « name »'
                }

                if (field.type == 'string') {
                    // We are happy
                    fields.push({
                        type: 'string',
                        tag: field.tag,
                        name: field.name,
                    })
                    order.push(field.tag)
                } else if (field.type == 'choice') {
                    if (!field.choices || field.choices.length == 0) {
                        error = 'Champ manquant ou invalide : « choices »'
                        return
                    }
                    const choices: { tag: string; text: string }[] = []
                    for (const choice of field.choices) {
                        if (!choice.tag || !choice.text) {
                            error = 'Choix manquant un champ « tag » ou un « text »'
                            return
                        }
                        choices.push({
                            tag: choice.tag,
                            text: choice.text,
                        })
                    }
                    fields.push({
                        type: 'choice',
                        tag: field.tag,
                        name: field.name,
                        choices: choices,
                    })
                    order.push(field.tag)
                } else {
                    error = `Type de champ inconnu, « ${field.type} »`
                    return
                }
            }

            config.fields = fields
            config.field_order = order
        }
    }

    async function saveConfig() {
        saving = true

        let r: any
        try {
            r = await simpleFetch('PUT', '/json/wdom-user/1/extra-meta/config', {
                order: cloneDeep(config.field_order),
                fields: cloneDeep(config.fields),
            })
            notify({
                type: 'success',
                title: 'Métadonnées',
                text: 'Succès de sauvegarde.',
            })
        } catch (e) {
            console.error('Error:', e)
            notify({
                type: 'danger',
                title: 'Métadonnées',
                text: 'La sauvegarde a généré une erreur.',
            })
        }

        saving = false
    }

    let expandEditor = $state(false)

    const exampleText = `[
    {
        "name": "Texte question 1",
        "tag": "q1",
        "type": "string"
    },
    {
        "name": "Texte question 2",
        "tag": "q2",
        "type": "choice",
        "choices": [
            {"tag": "-10", "text": "10 ans et moins"},
            {"tag": "11-20", "text": "11 à 20 ans"},
            {"tag": "21-", "text": "21 ans et plus"}
        ]
    }
]`
</script>

<Breadcrumb {crumbs} />

<div class="d-grid gap-3">
    <div class="card">
        <div class="card-header text-bg-primary">
            <div class="d-flex">
                <div style="flex: 1 0 0; margin-right: 1em">Configuration</div>
                <div style="flex: 0 0 auto">
                    <button class="button btn btn-outline-secondary btn-sm" type="button" class:active={expandEditor} onclick={() => (expandEditor = !expandEditor)}><Icon inline icon="fa:arrows-v" /></button>
                </div>
            </div>
        </div>
        {#if loading}
            <div class="card-body">
                <LoadingPattern pattern={['x', 'x', 'x', 'x', 'x']} />
            </div>
        {:else}
            <div class="card-body extra-meta-config" class:limit-height={!expandEditor}>
                <CodeMirror
                    options={{ theme: 'ttcn', lineWrapping: true, lineNumbers: true, readOnly: false, mode: { name: 'javascript', json: true }, viewportMargin: Infinity }}
                    {value}
                    setValue={(v) => {
                        value = v
                        validate(value)
                    }}
                />
            </div>

            {#if error}
                <div class="card-body">
                    <p>Erreur: <span>{error}</span></p>
                </div>
            {/if}

            <div class="card-body">
                <button type="button" class="btn btn-primary" onclick={() => saveConfig()} disabled={saving || loading || !!error}>Sauvegarder</button>
            </div>
        {/if}
    </div>

    <div class="card">
        <div class="card-header text-bg-info">Mini aide</div>
        <div class="card-body">
            <p>Syntaxe: JSON</p>
            <p>Il s’agit d’une liste (<code>[ …, …, … ]</code>) d’objets (<code>&lbrace; "clef": "valeur", "clef 2": "valeur 2", …&rbrace;</code>), chacun ayant les propriétés :</p>
            <ul>
                <li><code>type</code>,</li>
                <li><code>name</code> : texte affiché,</li>
                <li><code>tag</code> : code unique pour la correspondance avec la réponse.</li>
            </ul>
            <p>Les types supportées peuvent ajouter des propriétés. Types :</p>
            <p><strong>Type = "<code>string</code>"</strong>. Aucune propriété supplémentaire. La réponse est simplement une chaîne de caractères.</p>
            <p><strong>Type = "<code>choice</code>"</strong>. Choix de réponse. On a une propriété supplémentaire qui est une <em>liste d’objets</em> ayant les propriétés suivantes :</p>
            <ul>
                <li><code>tag</code> : identificateur unique utilisé comme réponse,</li>
                <li><code>text</code> : texte affiché.</li>
            </ul>
        </div>
    </div>

    <div class="card">
        <div class="card-header text-bg-info">Exemple</div>
        <div class="card-body">
            <CodeMirror options={{ theme: 'ttcn', readOnly: 'nocursor', mode: { name: 'javascript', json: true } }} value={exampleText} />
        </div>
    </div>
</div>
